import type { Json } from "@/lib/database.types";
import {
  appendPhotoTipsToFeedbackSummary,
  isListingPhotoFeedbackSlug,
} from "@/lib/listingPhotoFeedback";
import {
  parsePriceEscapeHatchFromJson,
  parsePriceOptionsFromJson,
} from "@/lib/listing-review-rounds/pricePhase";
import type { ItemReviewRoundPhase } from "@/lib/listing-review-rounds/types";
import type { SellerReviewFeedback } from "@/lib/types";

/** JSON shape stored in items.seller_review_feedback (snake_case keys from API/DB) */
export interface SellerReviewFeedbackRow {
  reason_codes: string[];
  preset_messages: string[];
  admin_note?: string | null;
  updated_at: string;
  created_by?: string | null;
  /** Admin-marked presets that no longer require seller evidence (Phase 3). */
  satisfied_presets?: string[];
  /** Phase within mixed review — mirrors open item_review_rounds.phase (Phase 4). */
  review_phase?: "non_price" | "price_decision" | "complete" | null;
  /** Admin-entered price options when pricing_issues preset selected (Phase 4). */
  price_options?: Array<{
    amount_ngn: number;
    label?: string | null;
    explanation?: string | null;
  }> | null;
  /** Optional one-time counter band when admin enables escape hatch (Phase 4). */
  price_escape_hatch?: {
    enabled: boolean;
    band_min_ngn: number;
    band_max_ngn: number;
    counter_used: boolean;
  } | null;
  /** Mixed accept: seller chose a price; admin still reviews other fixes. */
  price_seller_decision?: {
    action: "accept";
    accepted_option_index: number;
    amount_ngn: number;
    at: string;
  } | null;
}

export function parseSellerReviewFeedback(
  raw: Json | null | undefined,
): SellerReviewFeedback | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const reasonCodes = o.reason_codes;
  const presetMessages = o.preset_messages;
  const updatedAt = o.updated_at;
  if (!Array.isArray(reasonCodes) || !Array.isArray(presetMessages))
    return null;
  if (typeof updatedAt !== "string") return null;
  const codes = reasonCodes.filter((x): x is string => typeof x === "string");
  const messages = presetMessages.filter(
    (x): x is string => typeof x === "string",
  );
  const satisfiedRaw = o.satisfied_presets;
  const satisfiedPresets = Array.isArray(satisfiedRaw)
    ? satisfiedRaw.filter((x): x is string => typeof x === "string")
    : undefined;
  const reviewPhaseRaw = o.review_phase;
  const reviewPhase =
    reviewPhaseRaw === "non_price" ||
    reviewPhaseRaw === "price_decision" ||
    reviewPhaseRaw === "complete"
      ? (reviewPhaseRaw as ItemReviewRoundPhase)
      : undefined;
  return {
    reasonCodes: codes,
    presetMessages: messages,
    adminNote:
      typeof o.admin_note === "string" || o.admin_note === null
        ? (o.admin_note as string | null)
        : undefined,
    updatedAt,
    createdBy:
      typeof o.created_by === "string" || o.created_by === null
        ? (o.created_by as string | null)
        : undefined,
    satisfiedPresets:
      satisfiedPresets && satisfiedPresets.length > 0
        ? satisfiedPresets
        : undefined,
    reviewPhase,
    priceOptions: parsePriceOptionsFromJson(o.price_options) ?? undefined,
    priceEscapeHatch:
      parsePriceEscapeHatchFromJson(o.price_escape_hatch) ?? undefined,
    priceSellerDecision: parsePriceSellerDecisionFromJson(
      o.price_seller_decision,
    ),
  };
}

function parsePriceSellerDecisionFromJson(
  raw: unknown,
): SellerReviewFeedback["priceSellerDecision"] {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  if (o.action !== "accept") return undefined;
  const index = o.accepted_option_index;
  const amount = o.amount_ngn;
  const at = o.at;
  if (
    typeof index !== "number" ||
    !Number.isInteger(index) ||
    index < 0 ||
    typeof amount !== "number" ||
    !Number.isFinite(amount) ||
    amount <= 0 ||
    typeof at !== "string"
  ) {
    return undefined;
  }
  return {
    action: "accept",
    accepted_option_index: index,
    amount_ngn: Math.round(amount),
    at,
  };
}

export function toSellerReviewFeedbackJson(
  payload: SellerReviewFeedbackRow,
): Json {
  return payload as unknown as Json;
}

/** Normalized bullet lines for seller UI (handles legacy multi-line preset rows). */
export function getSellerReviewFeedbackDisplayLines(
  feedback: SellerReviewFeedback | null | undefined,
): string[] {
  if (!feedback?.presetMessages?.length) {
    return [];
  }
  return flattenPresetMessagesForDisplay(feedback.presetMessages)
    .map((line) => line.replace(/^\*\s*/, "").trim())
    .filter(Boolean);
}

export type SellerReviewFeedbackDomainGroup = {
  photos: string[];
  details: string[];
  /** True when pricing_issues is present and admin sent price options. */
  showPricing: boolean;
};

function lineLooksLikePhoto(line: string): boolean {
  const lower = line.toLowerCase();
  return (
    lower.includes("photo") ||
    lower.includes("image") ||
    lower.includes("gallery") ||
    lower.includes("cover")
  );
}

function lineLooksLikePricing(line: string): boolean {
  const lower = line.toLowerCase();
  return (
    lower.includes("pric") || lower.includes("naira") || lower.includes("₦")
  );
}

/**
 * Split seller feedback into domain sections for a single "Requested changes" UI.
 * Pricing bullets are omitted when options exist — the price decision card owns that domain.
 */
export function groupSellerReviewFeedbackByDomain(
  feedback: SellerReviewFeedback | null | undefined,
): SellerReviewFeedbackDomainGroup {
  if (!feedback?.reasonCodes?.length) {
    return { photos: [], details: [], showPricing: false };
  }

  const codes = feedback.reasonCodes;
  const showPricing =
    codes.includes("pricing_issues") && Boolean(feedback.priceOptions?.length);

  const hasPhotos = codes.some(isListingPhotoFeedbackSlug);
  const hasDetails = codes.some(
    (code) => code !== "pricing_issues" && !isListingPhotoFeedbackSlug(code),
  );

  const nonPricingLines = getSellerReviewFeedbackDisplayLines(feedback).filter(
    (line) => !lineLooksLikePricing(line),
  );

  if (hasPhotos && hasDetails) {
    const photos = nonPricingLines.filter(lineLooksLikePhoto);
    const details = nonPricingLines.filter((line) => !lineLooksLikePhoto(line));
    return {
      photos: photos.length > 0 ? photos : nonPricingLines,
      details,
      showPricing,
    };
  }
  if (hasPhotos) {
    return { photos: nonPricingLines, details: [], showPricing };
  }
  if (hasDetails) {
    return { photos: [], details: nonPricingLines, showPricing };
  }
  // Pricing-only (or unknown codes): no non-price bullets
  return { photos: [], details: [], showPricing };
}

/**
 * Splits a preset seller_message into one or more bullet lines when admins
 * author multi-line copy (each line starting with "* ").
 */
export function splitSellerPresetBullets(sellerMessage: string): string[] {
  const trimmed = sellerMessage.trim();
  if (!trimmed) {
    return [];
  }
  const lines = trimmed
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length <= 1) {
    return [trimmed];
  }
  if (lines.every((line) => line.startsWith("*"))) {
    return lines;
  }
  return [trimmed];
}

/** Expands stored preset_messages for seller UI and outbound channels. */
export function flattenPresetMessagesForDisplay(
  presetMessages: string[],
): string[] {
  return presetMessages.flatMap(splitSellerPresetBullets);
}

export type FormatStructuredFeedbackOptions = {
  reasonCodes?: string[];
};

/**
 * Plain-text summary for notifications, WhatsApp, and templates ({feedback_summary}).
 * Matches seller-facing bullet content (leading "* " stripped like ItemDetail).
 */
export function formatStructuredFeedbackSummaryPlain(
  presetMessages: string[],
  adminNote?: string | null,
  options?: FormatStructuredFeedbackOptions,
): string {
  const lines = flattenPresetMessagesForDisplay(presetMessages)
    .map((line) => line.replace(/^\*\s*/, "").trim())
    .filter(Boolean);
  const bulletBlock = lines.map((line) => `• ${line}`).join("\n");
  const trimmedNote = adminNote?.trim();
  const note = trimmedNote ? `\n\nAdditional note: ${trimmedNote}` : "";
  const base = `${bulletBlock}${note}`.trim();
  if (options?.reasonCodes?.some(isListingPhotoFeedbackSlug)) {
    return appendPhotoTipsToFeedbackSummary(base, options.reasonCodes);
  }
  return base;
}

/**
 * Expands raw DB preset seller_message values into the stored preset_messages array.
 */
export function expandPresetMessagesFromSlugs(
  reasonCodes: string[],
  messagesBySlug: Map<string, string>,
): string[] {
  const preset_messages: string[] = [];
  for (const code of reasonCodes) {
    const msg = messagesBySlug.get(code);
    if (!msg) {
      continue;
    }
    preset_messages.push(...splitSellerPresetBullets(msg));
  }
  return preset_messages;
}
